import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';

import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import { VendorService } from '../../services/vendor.service';
import { VendorPaymentModalComponent } from '../vendor-payment-modal/vendor-payment-modal.component';

/** Which grid is on screen. Also decides what the exports contain. */
export type OpenPayablesView = 'vendor' | 'documents';

/**
 * Open Payables — every supplier document still carrying a balance, across all
 * vendors, for an invoice-date range.
 *
 * Reads apir/vendor/openpayables. Open is `ovamount + ovgst + ovfee - alloc`, the
 * A/P aging's own expression — not the `settled` flag, which drifts (91 documents
 * on Clevehill are flagged settled while still owing).
 *
 * Two dates, two questions, which is the whole point of the screen:
 *
 *   From / To   the invoice's own date — "what did we raise between January and now"
 *   Due by      optional — "and of that, what was already due by the 10th"
 *
 * Leave Due by empty and you get everything open in the window; fill it in and you
 * get the subset that had fallen due by that date. It narrows the same set, so the
 * tiles, the aging strip and the grid always describe one population.
 *
 * What it does NOT do is rewind the ledger. `alloc` is today's allocation, so this
 * is what is open NOW, invoiced in that window. "What was open as of a past date"
 * is a different query — weberp/vendor::trialBalanceAsOf rebuilds the allocation
 * from suppallocs.datealloc for that.
 *
 * Two views, and the document list is the default
 * -----------------------------------------------
 * "Open payables from X to Y" is a question about documents, so that is what the
 * screen opens on. The page is capped (a default Clevehill run is ~13,000 documents)
 * but the tiles, the aging strip and every export cover the whole range regardless.
 *
 * The vendor roll-up is the second view, for when 13,000 rows is unreadable and you
 * want the exposure instead — TireHub net -$316,874, Vans $884,981 at 90+. It is
 * deliberately NOT the default: it is close to what the Over Due Vendors dashboard
 * card already shows, and that card should not be this report's front door.
 *
 * Sibling screens: Over Due Vendors is this ledger summarised per vendor and
 * hardcoded to `duedate <= today`, so it sees only the past-due slice (~$1.9M of
 * ~$5.7M) and cannot take a date range. The vendor's own Transactions tab is this
 * detail for one account. This is the all-vendor, parameterised view neither gives,
 * and it hands off to the same pay flow the dashboard card uses.
 */
@Component({
	selector: 'app-open-payables',
	templateUrl: './open-payables.component.html',
	styleUrls: ['./open-payables.component.scss']
})
export class OpenPayablesComponent implements OnInit, OnDestroy {

	/**
	 * What an export is allowed to ask for. Matches the endpoint's own ceiling: an
	 * export capped below the population produces a file whose rows cannot add up to
	 * its own total line, which is how a correct report gets blamed for being wrong.
	 */
	private static readonly EXPORT_LIMIT = 50000;

	/**
	 * Above this many documents the pdf prints the vendor summary instead of the
	 * detail. 13,000 rows is roughly two hundred pages — a stack nobody reads, and a
	 * wkhtmltopdf render nobody waits for. The xlsx has no such limit; that is what a
	 * spreadsheet is for.
	 */
	private static readonly PDF_DETAIL_MAX = 3000;

	private destroy$ = new Subject<void>();

	filterForm: UntypedFormGroup;
	searcher = new UntypedFormControl('');

	view: OpenPayablesView = 'documents';

	loading = false;
	exporting = false;
	errorMessage = '';

	/** Vendor roll-up — the whole filtered set, never capped. ~36 rows. */
	vendorRows: any[] = [];
	vendorResults: any[] = [];

	/** Documents — capped by the row cap, which is why the roll-up leads. */
	rows: any[] = [];
	searchresults: any[] = [];

	totals: any = null;
	buckets: any[] = [];
	truncated = false;
	limit = 500;
	asof = '';

	/** Only vendors that actually owe something — 36 of them, not the whole master. */
	vendors: any[] = [];
	rowcaps = [500, 1000, 2500, 5000];

	sort = '';
	dir: 'asc' | 'desc' = 'desc';
	vsort = '';
	vdir: 'asc' | 'desc' = 'desc';

	/** Expanded vendor in the roll-up, and its documents (fetched on demand). */
	expandedVendor: string | null = null;
	vendorDocs: any[] = [];
	vendorDocsLoading = false;
	vendorDocsTruncated = false;

	/** Selected document ids, for the pay hand-off. Keyed by supptrans.id. */
	selected: { [id: number]: any } = {};

	constructor(
		private fb: UntypedFormBuilder,
		private vendorService: VendorService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private modalService: NgbModal,
		private location: Location,
		private route: ActivatedRoute,
	) {}

	ngOnInit(): void {
		const today = new Date();
		const jan = new Date(today.getFullYear(), 0, 1);
		const q = this.route.snapshot.queryParams || {};

		// Filters come off the URL when there is one, so a run can be bookmarked and
		// sent to someone else. Anything absent or malformed falls back to the default
		// rather than being trusted into the request.
		this.filterForm = this.fb.group({
			from: [this.validDate(q['from']) ? q['from'] : this.isoDate(jan)],
			to: [this.validDate(q['to']) ? q['to'] : this.isoDate(today)],
			dueby: [this.validDate(q['dueby']) ? q['dueby'] : ''],
			doctype: [this.oneOf(q['doctype'], ['all', 'invoices', 'credits'], 'all')],
			supplierno: [this.csv(q['supplierno'])],
			bucket: [q['bucket'] || ''],
			heldonly: [q['heldonly'] === '1'],
			search: [q['search'] || ''],
			limit: [this.oneOfNum(q['limit'], this.rowcaps, 500)],
		});

		this.view = this.oneOf(q['view'], ['vendor', 'documents'], 'documents') as OpenPayablesView;
		this.sort = q['sort'] || '';
		this.dir = q['dir'] === 'asc' ? 'asc' : 'desc';
		this.vsort = q['vsort'] || '';
		this.vdir = q['vdir'] === 'asc' ? 'asc' : 'desc';

		this.searcher.valueChanges.pipe(
			debounceTime(200),
			distinctUntilChanged(),
			takeUntil(this.destroy$)
		).subscribe(() => this.filterSearch());

		this.vendorService.getOpenPayablesFilters().subscribe((r: any) => {
			if (!r?.success) { return; }
			this.vendors = r.vendors || [];
		});

		this.load();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	load(): void {
		this.loading = true;
		this.errorMessage = '';
		this.collapseVendor();
		this.clearSelection();
		this.syncUrl();

		this.vendorService.getOpenPayables(this.params()).subscribe((r: any) => {
			this.loading = false;
			if (!r?.success) {
				this.reset();
				this.errorMessage = r?.message || 'Could not load open payables for this range.';
				return;
			}
			this.rows = r.rows || [];
			this.vendorRows = r.vendors || [];
			this.totals = r.totals || null;
			this.buckets = r.buckets || [];
			this.truncated = !!r.truncated;
			this.limit = r.limit || this.limit;
			this.asof = r.asof || '';
			this.filterSearch();
		}, () => {
			this.loading = false;
			this.reset();
			this.errorMessage = 'Could not load open payables for this range.';
		});
	}

	private reset(): void {
		this.rows = [];
		this.vendorRows = [];
		this.searchresults = [];
		this.vendorResults = [];
		this.totals = null;
		this.buckets = [];
		this.rebuildGroups();
	}

	/**
	 * The filter state in the address bar, replaced rather than pushed — this is the
	 * same report being re-run, not a new page, and a Back button that walks through
	 * every filter change is a worse Back button.
	 *
	 * replaceState, not router.navigate: navigating to the route you are already on
	 * re-runs the guard and remounts the component, which would clear the grid you
	 * just filtered.
	 */
	private syncUrl(): void {
		const p = this.params();
		const parts: string[] = [];

		Object.keys(p).forEach((k) => {
			const v = p[k];
			if (v === null || v === undefined || v === '') { return; }
			if (k === 'limit' && Number(v) === 500) { return; }
			parts.push(k + '=' + encodeURIComponent(v));
		});
		if (this.view !== 'documents') { parts.push('view=' + this.view); }

		this.location.replaceState('/vendors/open-payables', parts.join('&'));
	}

	// ---- view ---------------------------------------------------------------

	setView(view: OpenPayablesView): void {
		if (this.view === view) { return; }
		this.view = view;
		this.collapseVendor();
		this.clearSelection();
		this.searcher.setValue('', { emitEvent: false });
		this.filterSearch();
		this.syncUrl();
	}

	/** Client-side overlay on what is loaded — instant, no round trip. */
	filterSearch(): void {
		const term = (this.searcher.value || '').toString().trim();
		if (!term) {
			this.searchresults = this.rows;
			this.vendorResults = this.vendorRows;
			this.rebuildGroups();
			return;
		}
		this.searchresults = this.globalSearchService.filterItem(this.rows, term,
			'supplierno,suppname,transno,suppreference,typename,transtext');
		this.vendorResults = this.globalSearchService.filterItem(this.vendorRows, term,
			'supplierno,suppname,termsname');
		this.rebuildGroups();
	}

	get searching(): boolean {
		return !!(this.searcher.value || '').toString().trim();
	}

	// ---- date presets -------------------------------------------------------

	/**
	 * The three questions actually asked at a check run. "Due by the 10th" stays
	 * available in the date field — this is just the 90% case without a date picker.
	 */
	get datePresets(): { key: string; label: string; date: string }[] {
		const today = new Date();
		const endOfWeek = new Date(today);
		// Through the coming Sunday: a check run is planned for the week, and a
		// Wednesday "this week" that stopped on Wednesday would be useless.
		endOfWeek.setDate(today.getDate() + (7 - today.getDay()) % 7);
		const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

		return [
			{ key: 'now', label: 'Overdue now', date: this.isoDate(today) },
			{ key: 'week', label: 'Due this week', date: this.isoDate(endOfWeek) },
			{ key: 'month', label: 'Due by month-end', date: this.isoDate(endOfMonth) },
		];
	}

	applyPreset(preset: { date: string }): void {
		this.filterForm.patchValue({ dueby: preset.date });
		this.load();
	}

	isPresetActive(preset: { date: string }): boolean {
		return this.filterForm?.value?.dueby === preset.date;
	}

	clearDueBy(): void {
		this.filterForm.patchValue({ dueby: '' });
		this.load();
	}

	// ---- aging drill --------------------------------------------------------

	/**
	 * Clicking a bucket narrows everything else to it. The strip itself keeps showing
	 * all six — it is the control, so collapsing it to the one you picked would leave
	 * no way back to the others.
	 */
	selectBucket(bucket: any): void {
		const current = this.filterForm.value.bucket;
		this.filterForm.patchValue({ bucket: current === bucket.key ? '' : bucket.key });
		this.load();
	}

	get activeBucket(): string {
		return this.filterForm?.value?.bucket || '';
	}

	isBucketActive(bucket: any): boolean {
		return this.activeBucket === bucket.key;
	}

	clearBucket(): void {
		this.filterForm.patchValue({ bucket: '' });
		this.load();
	}

	bucketLabel(key: string): string {
		const b = (this.buckets || []).find((x: any) => x.key === key);
		return b ? b.label : '';
	}

	// ---- vendor roll-up -----------------------------------------------------

	/**
	 * Expand a vendor to its documents. Fetched per vendor rather than sliced out of
	 * the loaded page — the page is capped at 500 and Vans alone has 2,387 open
	 * documents, so slicing would quietly show a fraction of the vendor you clicked.
	 */
	toggleVendor(row: any): void {
		if (this.expandedVendor === row.supplierno) {
			this.collapseVendor();
			return;
		}

		this.expandedVendor = row.supplierno;
		this.vendorDocs = [];
		this.vendorDocsTruncated = false;
		this.vendorDocsLoading = true;

		this.vendorService.getOpenPayables(this.params({
			supplierno: row.supplierno,
			limit: OpenPayablesComponent.EXPORT_LIMIT,
		})).subscribe((r: any) => {
			this.vendorDocsLoading = false;
			if (!r?.success) { return; }
			this.vendorDocs = r.rows || [];
			this.vendorDocsTruncated = !!r.truncated;
		}, () => { this.vendorDocsLoading = false; });
	}

	collapseVendor(): void {
		this.expandedVendor = null;
		this.vendorDocs = [];
		this.vendorDocsTruncated = false;
	}

	isVendorExpanded(row: any): boolean {
		return this.expandedVendor === row.supplierno;
	}

	/** Aging cells on a vendor row, in ladder order, zeros dropped to keep it scannable. */
	vendorBuckets(row: any): { key: string; label: string; amount: number }[] {
		return (this.buckets || [])
			.map((b: any) => ({ key: b.key, label: b.label, amount: Number(row.buckets?.[b.key]) || 0 }))
			.filter((b) => !this.isZero(b.amount));
	}

	// ---- selection & pay ----------------------------------------------------

	// ---- documents view: grouped + focused ---------------------------------
	//
	// A flat 500-row document list is unusable when the job is "pay Van's four
	// invoices": that vendor's rows are scattered through everyone else's. So the
	// documents grid groups by vendor behind the same expandable drawer the roll-up
	// uses, and a Picked-only chip narrows it to what is already ticked.
	//
	// Both are display only — the tiles, the totals row and every export keep
	// reading the loaded rows in full.

	/** Group the documents grid by vendor (default) or show it flat. */
	groupDocs = true;
	/** Focused list: only documents that are ticked. */
	pickedOnly = false;
	/** The grouped grid. Rebuilt on load, search and every selection change. */
	docGroups: any[] = [];
	/** Expanded vendor groups in the documents grid, by supplierno. */
	private openGroups = new Set<string>();
	/** Which group set was auto-expanded, so a deliberate collapse is not undone. */
	private autoExpandedFor = '';
	/** Rows the focused view keeps listed after an untick, so nothing vanishes. */
	private focusIds = new Set<number>();
	/** Expand every group up to this many — beyond it, opening all defeats the point. */
	static readonly AUTO_EXPAND_GROUPS = 3;

	setGroupDocs(on: boolean): void {
		this.groupDocs = on;
		this.rebuildGroups();
	}

	togglePickedOnly(): void {
		this.pickedOnly = !this.pickedOnly;
		if (this.pickedOnly) {
			// Unticking a row inside the focused list must not make it disappear before
			// you can put it back, so the rows in focus are snapshotted on entry.
			this.focusIds = new Set(this.selectedRows.map((r) => Number(r.id)));
		}
		this.rebuildGroups();
	}

	/** The document rows the grid renders, before grouping. */
	get docRows(): any[] {
		const rows = this.searchresults || [];
		if (!this.pickedOnly) { return rows; }
		return rows.filter((r) => this.isSelected(r) || this.focusIds.has(Number(r.id)));
	}

	/**
	 * Group the visible documents by vendor, in the order the grid's sort put them so
	 * a column sort still means something. Each group header carries what you need to
	 * decide whether to open it: how many documents, how much is open, what is picked.
	 */
	private rebuildGroups(): void {
		const groups: any[] = [];
		const byVendor: { [k: string]: any } = {};

		for (const r of this.docRows) {
			const key = r.supplierno || '';
			let g = byVendor[key];
			if (!g) {
				g = {
					supplierno: key,
					suppname: r.suppname,
					docs: [],
					open: 0,
					picked: 0,
					pickedTotal: 0,
					held: 0,
					oldest: null,
				};
				byVendor[key] = g;
				groups.push(g);
			}
			g.docs.push(r);
			g.open += Number(r.open_balance) || 0;
			if (r.hold) { g.held++; }
			if (this.isSelected(r)) {
				g.picked++;
				g.pickedTotal += Number(r.open_balance) || 0;
			}
			const due = r.duedate || r.trandate;
			if (due && (!g.oldest || due < g.oldest)) { g.oldest = due; }
		}

		this.docGroups = groups;
		this.autoExpandGroups(groups);
	}

	/**
	 * One vendor, or a handful, opens itself — a single collapsed group is a click for
	 * nothing. Anything larger opens only the groups that already have picks in them.
	 * Keyed by the group set so re-running on a selection change doesn't reopen what
	 * the operator just collapsed.
	 */
	private autoExpandGroups(groups: any[]): void {
		const key = groups.map((g) => g.supplierno).join('|');
		if (key === this.autoExpandedFor) { return; }
		this.autoExpandedFor = key;
		this.openGroups = groups.length && groups.length <= OpenPayablesComponent.AUTO_EXPAND_GROUPS
			? new Set(groups.map((g) => g.supplierno))
			: new Set(groups.filter((g) => g.picked > 0).map((g) => g.supplierno));
	}

	toggleGroup(g: any): void {
		if (this.openGroups.has(g.supplierno)) {
			this.openGroups.delete(g.supplierno);
		} else {
			this.openGroups.add(g.supplierno);
		}
	}

	isGroupOpen(g: any): boolean {
		return this.openGroups.has(g.supplierno);
	}

	expandAllGroups(): void {
		this.openGroups = new Set(this.docGroups.map((g) => g.supplierno));
	}

	collapseAllGroups(): void {
		this.openGroups = new Set<string>();
	}

	get allGroupsOpen(): boolean {
		return this.docGroups.length > 0 && this.docGroups.every((g) => this.isGroupOpen(g));
	}

	/**
	 * What can be ticked, and why the rest cannot.
	 *
	 * An open invoice is what you pay. A credit note or a payment on account is what
	 * you pay it WITH — the pay screen carries both as on-account credit, so ticking
	 * one here nets it off the payment instead of writing a bigger cheque.
	 *
	 * A held document is out either way: the pay screen loads `settled = 0 AND
	 * hold = 0`, so it would arrive there with nothing to land on.
	 */
	isInvoiceRow(row: any): boolean {
		return +row?.type === 20 && Number(row?.open_balance) > 0.004;
	}

	isCreditRow(row: any): boolean {
		return (+row?.type === 21 || +row?.type === 22)
			&& Number(row?.open_balance) < -0.004;
	}

	isSelectable(row: any): boolean {
		return !+row?.hold && (this.isInvoiceRow(row) || this.isCreditRow(row));
	}

	/** Why a row's checkbox is disabled, for its tooltip. */
	unselectableReason(row: any): string {
		if (this.isSelectable(row)) { return ''; }
		if (+row?.hold) { return 'On hold — release the hold before paying it'; }
		return 'Nothing open on this document';
	}

	toggleRow(row: any): void {
		if (!this.isSelectable(row)) { return; }
		if (this.selected[row.id]) {
			delete this.selected[row.id];
		} else {
			this.selected[row.id] = row;
		}
		this.rebuildGroups();
	}

	isSelected(row: any): boolean {
		return !!this.selected[row.id];
	}

	/** Header checkbox — "all shown" means all *selectable* shown. */
	selectableRows(rows: any[]): any[] {
		return (rows || []).filter((r) => this.isSelectable(r));
	}

	toggleAll(rows: any[]): void {
		const selectable = this.selectableRows(rows);
		if (this.allSelected(rows)) {
			selectable.forEach((r) => delete this.selected[r.id]);
		} else {
			selectable.forEach((r) => { this.selected[r.id] = r; });
		}
		this.rebuildGroups();
	}

	allSelected(rows: any[]): boolean {
		const selectable = this.selectableRows(rows);
		return selectable.length > 0 && selectable.every((r) => !!this.selected[r.id]);
	}

	clearSelection(): void {
		this.selected = {};
		this.pickedOnly = false;
		this.focusIds = new Set<number>();
		this.rebuildGroups();
	}

	get selectedRows(): any[] {
		return Object.keys(this.selected).map((k) => this.selected[Number(k)]);
	}

	get selectedInvoiceRows(): any[] {
		return this.selectedRows.filter((r) => this.isInvoiceRow(r));
	}

	get selectedCreditRows(): any[] {
		return this.selectedRows.filter((r) => this.isCreditRow(r));
	}

	get selectedInvoiceTotal(): number {
		return this.selectedInvoiceRows.reduce((a, r) => a + (Number(r.open_balance) || 0), 0);
	}

	/** Positive magnitude of the credit picked — it is stored as a negative balance. */
	get selectedCreditTotal(): number {
		return Math.abs(this.selectedCreditRows.reduce((a, r) => a + (Number(r.open_balance) || 0), 0));
	}

	/** Net cash: the invoices picked, less the credit picked to net against them. */
	get selectedTotal(): number {
		return this.selectedInvoiceTotal - this.selectedCreditTotal;
	}

	/**
	 * The vendor the selection belongs to, or null when it spans more than one.
	 * A payment is to one supplier, so a cross-vendor selection has no single pay
	 * action — the button says so rather than silently paying the first one.
	 */
	get selectedVendor(): any {
		const rows = this.selectedRows;
		if (!rows.length) { return null; }
		const first = rows[0].supplierno;
		return rows.every((r) => r.supplierno === first) ? rows[0] : null;
	}

	/**
	 * Credit covering the invoices picked is not a dead end — it is an application.
	 * The pay screen posts a $0 payment as credit allocations only (the endpoint
	 * writes no supptrans/G/L/bank leg for it), so the hand-off still opens; the
	 * button just stops calling it a payment.
	 */
	get creditCoversSelection(): boolean {
		return this.selectedInvoiceRows.length > 0
			&& this.selectedCreditRows.length > 0
			&& this.selectedTotal <= 0.004;
	}

	/** Cash the payment would actually be for — never negative; the rest is credit. */
	get payNetCash(): number {
		return Math.max(0, this.selectedTotal);
	}

	/** Credit picked beyond what the invoices can absorb — it stays on account. */
	get creditLeftOnAccount(): number {
		return Math.max(0, this.selectedCreditTotal - this.selectedInvoiceTotal);
	}

	get paySelectedLabel(): string {
		if (!this.selectedRows.length) { return 'Pay selected'; }
		if (!this.selectedVendor) { return 'Select one vendor to pay'; }
		// A credit on its own has nothing to land on — the pay screen applies credit
		// TO invoices, so at least one has to be picked.
		if (!this.selectedInvoiceRows.length) { return 'Pick an invoice to apply it to'; }
		if (this.creditCoversSelection) {
			return 'Apply ' + this.money(this.selectedInvoiceTotal) + ' credit at ' + this.selectedVendor.supplierno;
		}
		return 'Pay ' + this.money(this.selectedTotal) + ' to ' + this.selectedVendor.supplierno;
	}

	get canPaySelected(): boolean {
		return !!this.selectedVendor && this.selectedInvoiceRows.length > 0;
	}

	/** Paybar breakdown — only worth spelling out once a credit is in the mix. */
	get selectionSummary(): string {
		const inv = this.plural(this.selectedInvoiceRows.length, 'invoice');
		if (!this.selectedCreditRows.length) { return inv; }
		return inv + ' less ' + this.plural(this.selectedCreditRows.length, 'credit');
	}

	/**
	 * Hands off to the same modal the Over Due Vendors card uses.
	 *
	 * `docIds` is what makes "Pay selected" mean what it says: the pay screen ticks
	 * exactly those supptrans rows at their own open balance instead of running its
	 * oldest-first roll over the vendor's whole ledger, and the amount then follows
	 * the picks rather than a figure this report guessed. Without it (the vendor
	 * roll-up's row button) the pay screen keeps its own oldest-first behaviour.
	 * This report proposes; it does not post.
	 */
	payVendor(
		supplierno: string,
		suppname: string,
		amount: number,
		docIds: number[] = null,
		creditIds: number[] = null,
	): void {
		if (!supplierno) { return; }

		const ref = this.modalService.open(VendorPaymentModalComponent, {
			size: 'xl',
			scrollable: true,
			windowClass: 'vendor-payment-modal-window',
		});
		ref.componentInstance.supplierid = String(supplierno);
		ref.componentInstance.vendorTitle = `${suppname} · ${supplierno}`;
		ref.componentInstance.vendorName = suppname || '';
		ref.componentInstance.suggestedAmount = Math.abs(Number(amount) || 0);
		if (docIds && docIds.length) {
			ref.componentInstance.preselectIds = docIds;
		}
		if (creditIds && creditIds.length) {
			ref.componentInstance.preselectCreditIds = creditIds;
		}

		merge(ref.closed, ref.dismissed).pipe(take(1)).subscribe(() => this.load());
	}

	payFromVendorRow(row: any): void {
		// Past-due, not the whole balance: the default proposal is what is actually
		// late, which is what the dashboard card offers too.
		const amount = this.isZero(row.past_due) ? row.open_balance : row.past_due;
		this.payVendor(row.supplierno, row.suppname, amount);
	}

	paySelected(): void {
		if (!this.canPaySelected) { return; }
		const v = this.selectedVendor;
		const ids = this.selectedInvoiceRows.map((r) => Number(r.id)).filter((id) => !!id);
		const creditIds = this.selectedCreditRows.map((r) => Number(r.id)).filter((id) => !!id);
		// payNetCash, not the raw net: a credit bigger than the invoices makes the net
		// negative, and payVendor takes the absolute value — that would have opened the
		// screen asking for the credit's surplus as cash.
		this.payVendor(v.supplierno, v.suppname, this.payNetCash, ids, creditIds);
	}

	// ---- summary ------------------------------------------------------------

	/**
	 * Tiles follow what is on screen: server totals for the whole range normally,
	 * recomputed from the visible rows while the quick search is narrowing them —
	 * range totals printed above a filtered grid read as an arithmetic error.
	 */
	get displayTotals(): any {
		if (!this.searching) { return this.totals; }

		return this.view === 'vendor'
			? this.totalsFromVendors(this.vendorResults)
			: this.totalsFromDocs(this.searchresults);
	}

	private totalsFromDocs(rows: any[]): any {
		if (!rows.length) { return null; }

		const vendors: any = {};
		let open = 0;
		let total = 0;
		let alloc = 0;
		let pastDue = 0;
		let notDue = 0;
		let undated = 0;

		for (const r of rows) {
			vendors[r.supplierno] = true;
			const amount = Number(r.open_balance) || 0;
			open += amount;
			total += Number(r.totalamount) || 0;
			alloc += Number(r.allocated) || 0;
			// Same three-way split the server uses — an undated document belongs to
			// neither side, and folding it into one would put these tiles out of step
			// with the aging strip's own "No due date" bucket.
			if (r.age === null || r.age === undefined) {
				undated += amount;
			} else if (Number(r.age) >= 0) {
				pastDue += amount;
			} else {
				notDue += amount;
			}
		}

		return {
			docs: rows.length,
			vendors: Object.keys(vendors).length,
			totalamount: total,
			allocated: alloc,
			open_balance: open,
			past_due: pastDue,
			not_due: notDue,
			no_due_date: undated,
		};
	}

	private totalsFromVendors(rows: any[]): any {
		if (!rows.length) { return null; }

		const sum = (k: string) => rows.reduce((a: number, r: any) => a + (Number(r[k]) || 0), 0);

		return {
			docs: sum('docs'),
			vendors: rows.length,
			totalamount: sum('totalamount'),
			allocated: sum('allocated'),
			open_balance: sum('open_balance'),
			past_due: sum('past_due'),
			not_due: sum('not_due'),
			no_due_date: sum('no_due_date'),
		};
	}

	/**
	 * Undated documents hang off the "not yet due" tile rather than taking a fifth
	 * one: on every tenant checked it is a handful of rows, and a whole tile reading
	 * $0.00 on almost every run teaches people to ignore that row of the page.
	 */
	get undatedSub(): string | null {
		const t = this.displayTotals;
		if (!t || this.isZero(t.no_due_date)) { return null; }
		// No "plus" prefix: undated rows are as often credits as invoices, and
		// "plus -$754.52" is not a sentence anyone should have to parse.
		return this.money(t.no_due_date) + ' with no due date';
	}

	/** The aging strip. Empty buckets are dropped rather than printed as $0.00. */
	get displayBuckets(): any[] {
		return (this.buckets || []).filter((b: any) => b.docs > 0);
	}

	/** Share of the open balance in a bucket — drives the width of its bar. */
	bucketShare(bucket: any): number {
		const total = (this.buckets || [])
			.reduce((a: number, b: any) => a + Math.abs(Number(b.open_balance) || 0), 0);
		if (!total) { return 0; }
		return Math.min(100, Math.round((Math.abs(Number(bucket.open_balance) || 0) / total) * 100));
	}

	/** The summary line under the grid. */
	get totalsLabel(): string {
		const t = this.displayTotals;
		if (!t) { return ''; }

		const bits = [this.plural(t.docs, 'document'), this.plural(t.vendors, 'vendor')];
		const capped = this.truncated && !this.searching && this.view === 'documents';
		return (capped ? 'Range total — ' : '') + bits.join(' · ');
	}

	/** What the report is actually answering, said in words. */
	get scopeLine(): string {
		const f = this.filterForm.value;
		let line = 'Open as of ' + this.longDate(this.asof)
			+ ', invoiced ' + this.longDate(f.from) + ' to ' + this.longDate(f.to);
		if (f.dueby) { line += ', due by ' + this.longDate(f.dueby); }
		if (f.bucket) { line += ', ' + this.bucketLabel(f.bucket).toLowerCase(); }
		return line;
	}

	private plural(n: any, word: string): string {
		const count = Number(n) || 0;
		return count.toLocaleString('en-US') + ' ' + word + (count === 1 ? '' : 's');
	}

	// ---- row helpers -------------------------------------------------------

	/** Rounded to the cent: float noise on the ov* columns is not a balance. */
	isZero(v: any): boolean {
		return Math.abs(Number(v) || 0) < 0.005;
	}

	/**
	 * "112 days" / "in 9 days" / "—". Days past due, not days since the invoice: an
	 * invoice on 60-day terms is not late on day 45, and a column that said so would
	 * send someone chasing a bill nobody owes yet.
	 */
	ageLabel(row: any): string {
		if (row.age === null || row.age === undefined) { return '—'; }
		const age = Number(row.age);
		if (age < 0) { return 'in ' + this.plural(-age, 'day'); }
		if (age === 0) { return 'due today'; }
		return this.plural(age, 'day');
	}

	/** Colours the age cell by its aging bucket — same ladder as the strip above. */
	ageClass(row: any): string {
		switch (row.bucket) {
		case 'notdue': return 'op-notdue';
		case 'current': return 'op-due1';
		case 'over1': return 'op-due2';
		case 'over2': return 'op-due3';
		case 'over3': return 'op-due4';
		default: return 'op-blank';
		}
	}

	vendorLabel(v: any): string {
		return v.supplierno + ' — ' + v.suppname;
	}

	sortBy(key: string): void {
		if (this.sort === key) {
			this.dir = this.dir === 'desc' ? 'asc' : 'desc';
		} else {
			this.sort = key;
			this.dir = 'desc';
		}
		this.load();
	}

	sortIcon(key: string): string {
		if (this.sort !== key) { return ''; }
		return this.dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	vsortBy(key: string): void {
		if (this.vsort === key) {
			this.vdir = this.vdir === 'desc' ? 'asc' : 'desc';
		} else {
			this.vsort = key;
			this.vdir = 'desc';
		}
		this.load();
	}

	vsortIcon(key: string): string {
		if (this.vsort !== key) { return ''; }
		return this.vdir === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	// ---- export ------------------------------------------------------------

	get hasExportableRows(): boolean {
		return this.view === 'vendor' ? this.vendorResults.length > 0 : this.searchresults.length > 0;
	}

	private get exportTitle(): string {
		return 'Open Payables';
	}

	/**
	 * What shaped the file, including which grid it came from — a vendor summary and
	 * a document list are different reports and the subtitle has to say which one you
	 * are holding.
	 */
	private exportSubtitle(extra?: string): string {
		const f = this.filterForm.value;
		const bits = [this.scopeLine];
		bits.push(this.view === 'vendor' ? 'by vendor' : 'all documents');
		if (f.doctype === 'invoices') { bits.push('invoices only'); }
		if (f.doctype === 'credits') { bits.push('credits only'); }
		if (f.heldonly) { bits.push('held documents only'); }
		if ((f.supplierno || []).length) {
			bits.push(this.plural((f.supplierno || []).length, 'vendor') + ' selected');
		}
		if (f.search) { bits.push('matching "' + f.search + '"'); }
		if (this.searching) { bits.push('quick search "' + this.searcher.value + '"'); }
		if (extra) { bits.push(extra); }
		return bits.join('  ·  ');
	}

	private get exportFilename(): string {
		return 'open-payables-' + this.view + '-' + this.isoDate(new Date());
	}

	/**
	 * Every document the filter matched, not the page on screen.
	 *
	 * The grid is capped so the browser stays usable; a file is not. Exporting the
	 * capped page under the range's own total produced a spreadsheet whose rows summed
	 * to $7,833 beneath a total line reading $5.7M — the report looked wrong when it
	 * was right. So the export re-asks with the ceiling raised and builds from that.
	 *
	 * The quick search is deliberately NOT re-applied here: it filters what was
	 * loaded, and what was loaded is no longer what we have. Its use is called out in
	 * the subtitle, so a searched export says so and contains the full set.
	 */
	private withFullDetail(then: (rows: any[]) => void): void {
		if (!this.truncated) {
			then(this.searchresults);
			return;
		}

		this.vendorService.getOpenPayables(this.params({
			limit: OpenPayablesComponent.EXPORT_LIMIT,
		})).subscribe((r: any) => {
			then(r?.success ? (r.rows || []) : this.searchresults);
		}, () => then(this.searchresults));
	}

	exportXls(): void {
		this.exporting = true;

		if (this.view === 'vendor') {
			this.sendXls(this.buildVendorXlsHtml(this.vendorResults), this.exportSubtitle());
			return;
		}

		this.withFullDetail((rows) => {
			const note = rows.length > this.searchresults.length
				? this.plural(rows.length, 'document') + ' — full range, not the capped page'
				: '';
			this.sendXls(this.buildXlsHtml(rows), this.exportSubtitle(note));
		});
	}

	exportPdf(): void {
		this.exporting = true;

		if (this.view === 'vendor') {
			this.sendPdf(this.buildVendorPdfHtml(this.vendorResults), this.exportSubtitle());
			return;
		}

		this.withFullDetail((rows) => {
			// Two hundred pages of invoice lines is not a report. Above the threshold
			// the pdf prints the vendor summary and says why, so nobody is handed a
			// file that silently dropped nine tenths of its rows.
			if (rows.length > OpenPayablesComponent.PDF_DETAIL_MAX) {
				const note = this.plural(rows.length, 'document')
					+ ' — too many to print in full, summarised by vendor (use Excel for the detail)';
				this.sendPdf(this.buildVendorPdfHtml(this.vendorRows), this.exportSubtitle(note));
				return;
			}
			const note = rows.length > this.searchresults.length
				? this.plural(rows.length, 'document') + ' — full range, not the capped page'
				: '';
			this.sendPdf(this.buildPdfHtml(rows), this.exportSubtitle(note));
		});
	}

	private sendXls(html: string, subtitle: string): void {
		const data = {
			content: this.globalSearchService.base64encode(html),
			filename: this.exportFilename,
			title: this.exportTitle,
			subtitle,
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}

	private sendPdf(html: string, subtitle: string): void {
		const data = {
			content: this.globalSearchService.base64encode(html),
			filename: this.exportFilename,
			title: this.exportTitle,
			subtitle,
			// Nine columns plus the vendor banner do not fit portrait.
			landscape: true,
			// An A/P run gets printed and walked around; opt-in flag, so other
			// reports are left alone.
			footer: 1,
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}

	// ---- pdf ---------------------------------------------------------------

	/** The vendor summary, printed — one line per vendor, aging across. */
	private buildVendorPdfHtml(rows: any[]): string {
		const keys = (this.buckets || []).map((b: any) => b.key);

		let html = this.pdfStyles();
		html += this.pdfSummary();

		if (!rows.length) {
			return html + '<div class="op-empty">Nothing open in this range.</div>';
		}

		html += '<table class="opv"><thead><tr class="lbl">'
			+ '<td>Vendor</td><td>Name</td><td>Terms</td>'
			+ '<td class="n">Docs</td><td class="n">Open</td><td class="n">Past due</td>'
			+ keys.map((k: string) => '<td class="n">' + this.esc(this.bucketLabel(k)) + '</td>').join('')
			+ '</tr></thead><tbody>';

		for (const r of rows) {
			html += '<tr class="dt">'
				+ '<td>' + this.esc(r.supplierno || '—') + '</td>'
				+ '<td>' + this.esc(r.suppname) + '</td>'
				+ '<td>' + this.esc(r.termsname) + '</td>'
				+ '<td class="n">' + Number(r.docs).toLocaleString('en-US') + '</td>'
				+ '<td class="n strong">' + this.money(r.open_balance) + '</td>'
				+ '<td class="n' + (this.isZero(r.past_due) ? '' : ' late') + '">'
				+ this.money(r.past_due) + '</td>'
				+ keys.map((k: string) => {
					const v = Number(r.buckets?.[k]) || 0;
					return '<td class="n' + (this.isZero(v) ? ' dim' : '') + '">'
						+ (this.isZero(v) ? '' : this.money(v)) + '</td>';
				}).join('')
				+ '</tr>';
		}

		const t = this.totalsFromVendors(rows);
		html += '<tr class="gt"><td colspan="3">' + this.esc(this.plural(rows.length, 'vendor')) + '</td>'
			+ '<td class="n">' + Number(t.docs).toLocaleString('en-US') + '</td>'
			+ '<td class="n">' + this.money(t.open_balance) + '</td>'
			+ '<td class="n">' + this.money(t.past_due) + '</td>'
			+ keys.map((k: string) => '<td class="n">'
				+ this.money(rows.reduce((a: number, r: any) => a + (Number(r.buckets?.[k]) || 0), 0))
				+ '</td>').join('')
			+ '</tr>';

		return html + '</tbody></table>' + this.pdfGrandTotal(t);
	}

	/**
	 * The printed detail: grouped by vendor, because that is how it is worked. The
	 * screen repeats the vendor code and name on every line so the grid can be sorted
	 * any way you like; on paper that is five columns of the same word down the page,
	 * so the vendor moves into a band and its documents sit under it with a subtotal.
	 *
	 * Built from the data rather than cloned from the DOM, so the file is complete
	 * however the screen happens to be sorted or narrowed.
	 *
	 * One table per vendor for page breaks: a vendor that fits stays whole on a page,
	 * a 1,400-document vendor is allowed to split, and wkhtmltopdf repeats that
	 * table's own <thead> when it does — so the second page still has column labels.
	 */
	private buildPdfHtml(rows: any[]): string {
		const groups = this.groupByVendor(rows);
		const cols = this.pdfCols();
		const span = cols.length;
		const bannerLeft = Math.max(1, span - 3);

		let html = this.pdfStyles();
		html += this.pdfSummary();

		for (const g of groups) {
			html += '<div class="pg' + (g.rows.length > 14 ? ' pg-big' : '') + '">';
			html += '<table class="op"><colgroup>'
				+ cols.map((c: any) => '<col style="width:' + c.w + '%">').join('')
				+ '</colgroup>';

			html += '<thead>';
			html += '<tr class="ban">'
				+ '<td colspan="' + bannerLeft + '">'
				+ '<span class="b-code">' + this.esc(g.supplierno || '—') + '</span>'
				+ '<span class="b-name">' + this.esc(g.suppname) + '</span>'
				+ (g.termsname ? '<span class="b-terms">' + this.esc(g.termsname) + '</span>' : '')
				+ '</td>'
				+ '<td colspan="' + (span - bannerLeft) + '" class="ban-r">'
				+ '<div class="b-open"><span>Open</span> ' + this.money(g.open) + '</div>'
				+ '<div class="b-sub">' + this.esc(this.plural(g.rows.length, 'document'))
				+ (this.isZero(g.pastDue) ? '' : ' · <span class="b-late">'
					+ this.money(g.pastDue) + ' past due</span>')
				+ '</div>'
				+ '</td></tr>';
			html += '<tr class="lbl">'
				+ cols.map((c: any) => '<td' + (c.num ? ' class="n"' : '') + '>'
					+ this.esc(c.label) + '</td>').join('')
				+ '</tr>';
			html += '</thead><tbody>';

			for (const r of g.rows) {
				html += '<tr class="dt">'
					+ cols.map((c: any) => '<td' + this.pdfCellClass(c, r) + '>'
						+ c.cell(r) + '</td>').join('')
					+ '</tr>';
			}

			// Only earns its keep when the vendor has more than one document —
			// otherwise it repeats the line above it and the banner above that.
			if (g.rows.length > 1) {
				html += '<tr class="gt">'
					+ '<td colspan="' + (span - 3) + '">'
					+ this.esc(this.plural(g.rows.length, 'document')) + '</td>'
					+ '<td class="n">' + this.money(g.total) + '</td>'
					+ '<td class="n">' + this.money(g.allocated) + '</td>'
					+ '<td class="n">' + this.money(g.open) + '</td>'
					+ '</tr>';
			}

			html += '</tbody></table></div>';
		}

		if (!groups.length) {
			html += '<div class="op-empty">Nothing open in this range.</div>';
		}

		return html + this.pdfGrandTotal(this.totalsFromDocs(rows));
	}

	/**
	 * Vendor is in the banner, so nothing here repeats it. Ordered the way the grid
	 * is ordered so the file matches the screen it was exported from.
	 */
	private pdfCols(): any[] {
		return [
			{ label: 'Type', w: 12, cell: (r: any) => this.esc(r.typename) },
			{ label: 'Doc #', w: 9, cell: (r: any) => this.esc(r.transno) },
			{ label: 'Reference', w: 18, cell: (r: any) => this.esc(r.suppreference) },
			{ label: 'Invoiced', w: 9, cell: (r: any) => this.esc(this.usDate(r.trandate)) },
			{ label: 'Due', w: 9, cell: (r: any) => this.esc(r.duedate ? this.usDate(r.duedate) : '—') },
			{ label: 'Age', w: 10, age: true, cell: (r: any) => this.esc(this.ageLabel(r)) },
			{ label: 'Document total', w: 11, num: true, cell: (r: any) => this.money(r.totalamount) },
			{ label: 'Allocated', w: 11, num: true, dim: true, cell: (r: any) => this.money(r.allocated) },
			{ label: 'Open', w: 11, num: true, strong: true, cell: (r: any) => this.money(r.open_balance) },
		];
	}

	private pdfCellClass(col: any, r: any): string {
		const c = [];
		if (col.num) { c.push('n'); }
		if (col.dim) { c.push('dim'); }
		if (col.strong) { c.push('strong'); }
		// Only the genuinely late are reddened. Colouring every age turns the page
		// into a warning about invoices that are simply not due yet.
		if (col.age && r.age !== null && Number(r.age) > 0) { c.push('late'); }
		return c.length ? ' class="' + c.join(' ') + '"' : '';
	}

	/**
	 * Vendor groups in the order the rows already arrived, so the file follows
	 * whatever sort the screen is on rather than silently re-sorting itself.
	 */
	private groupByVendor(rows: any[]): any[] {
		const out: any[] = [];
		const index: any = {};

		for (const r of rows) {
			const key = r.supplierno || '';
			if (index[key] === undefined) {
				index[key] = out.length;
				out.push({
					supplierno: r.supplierno,
					suppname: r.suppname,
					termsname: r.termsname,
					rows: [],
					total: 0,
					allocated: 0,
					open: 0,
					pastDue: 0,
				});
			}
			const g = out[index[key]];
			g.rows.push(r);
			g.total += Number(r.totalamount) || 0;
			g.allocated += Number(r.allocated) || 0;
			g.open += Number(r.open_balance) || 0;
			if (r.age !== null && Number(r.age) >= 0) { g.pastDue += Number(r.open_balance) || 0; }
		}

		return out;
	}

	/** The tiles and the aging strip, printed — the shape of the range before the detail. */
	private pdfSummary(): string {
		const t = this.displayTotals;
		if (!t) { return ''; }

		const cell = (label: string, value: string, sub: string) =>
			'<td><div class="s-k">' + this.esc(label) + '</div>'
			+ '<div class="s-v">' + value + '</div>'
			+ '<div class="s-s">' + this.esc(sub) + '</div></td>';

		let html = '<table class="op-sum"><tr>'
			+ cell('Documents', Number(t.docs).toLocaleString('en-US'), this.plural(t.vendors, 'vendor'))
			+ cell('Open', this.money(t.open_balance), '')
			+ cell('Past due', this.money(t.past_due), '')
			+ cell('Not yet due', this.money(t.not_due), this.undatedSub || '')
			+ '</tr></table>';

		const buckets = this.displayBuckets;
		if (buckets.length > 1) {
			html += '<table class="op-age"><tr>'
				+ buckets.map((b: any) => '<td><div class="a-k">' + this.esc(b.label) + '</div>'
					+ '<div class="a-v">' + this.money(b.open_balance) + '</div>'
					+ '<div class="a-s">' + this.esc(this.plural(b.docs, 'doc')) + '</div></td>').join('')
				+ '</tr></table>';
		}

		return html;
	}

	private pdfGrandTotal(t: any): string {
		if (!t) { return ''; }

		return '<table class="op-grand"><tr>'
			+ '<td>' + this.esc(this.plural(t.docs, 'document') + ' · ' + this.plural(t.vendors, 'vendor')) + '</td>'
			+ '<td class="n">Past due ' + this.money(t.past_due) + '</td>'
			+ '<td class="n">Not yet due ' + this.money(t.not_due)
			+ (this.isZero(t.no_due_date) ? '' : ' · undated ' + this.money(t.no_due_date)) + '</td>'
			+ '<td class="n">Open ' + this.money(t.open_balance) + '</td>'
			+ '</tr></table>';
	}

	/**
	 * Print styles, inline because wkhtmltopdf renders this out of a shared header
	 * template that knows nothing about this report. Prefixed and `!important` where
	 * the template's own `td` rule would otherwise put its grid back on top of a
	 * layout built out of bands.
	 */
	private pdfStyles(): string {
		return '<style>'
			// The shared print header sets h5 heavier and larger than the h4 above it,
			// so the filter line shouts over the report name. Corrected here rather
			// than in the template, which every other export renders through.
			+ 'body h4{font-size:16px !important;font-weight:700 !important;color:#1F2B3E;'
			+ 'margin:0 0 2px !important;line-height:20px !important}'
			+ 'body h5{font-size:9.5px !important;font-weight:400 !important;color:#6B7A8D;'
			+ 'margin:0 0 8px !important;line-height:13px !important}'

			+ '.op-sum,.op-age{width:100%;border-collapse:separate;border-spacing:6px 0;'
			+ 'table-layout:fixed}'
			+ '.op-sum{margin:2px 0 6px}'
			+ '.op-age{margin:0 0 10px}'
			+ '.op-sum td,.op-age td{border:1px solid #D3DBE4 !important;'
			+ 'border-top:1px solid #D3DBE4 !important;background:#F5F7FA;'
			+ 'padding:6px 9px !important;vertical-align:top}'
			+ '.op-sum .s-k,.op-age .a-k{font-size:8px;letter-spacing:.08em;text-transform:uppercase;'
			+ 'color:#6B7A8D;line-height:11px}'
			+ '.op-sum .s-v{font-size:15px;font-weight:700;color:#1F2B3E;line-height:19px}'
			+ '.op-age .a-v{font-size:12px;font-weight:700;color:#1F2B3E;line-height:16px}'
			+ '.op-sum .s-s,.op-age .a-s{font-size:8px;color:#8494A5;line-height:11px}'

			+ '.op-empty{font-size:11px;color:#6B7A8D;text-align:center;padding:24px 0}'

			+ '.pg{page-break-inside:avoid;margin:0 0 7px}'
			+ '.pg-big{page-break-inside:auto}'
			+ 'table.op,table.opv{width:100%;border-collapse:collapse;table-layout:fixed;font-size:9px}'
			+ 'table.op td,table.opv td{padding:2px 5px !important;border-top:none !important;'
			+ 'line-height:12px;vertical-align:top;overflow:hidden}'
			+ 'table.op td.n,table.opv td.n{text-align:right}'

			+ 'table.op tr.ban td{background:#2C3A4B;color:#fff;padding:4px 7px !important;'
			+ 'border-bottom:1px solid #2C3A4B !important}'
			+ 'tr.ban .b-code{font-size:10px;font-weight:700}'
			+ 'tr.ban .b-name{font-size:10px;padding-left:8px}'
			+ 'tr.ban .b-terms{font-size:8px;color:#B9C7D6;padding-left:8px}'
			+ 'tr.ban td.ban-r{text-align:right}'
			+ 'tr.ban .b-open{font-size:12px;font-weight:700;line-height:15px}'
			+ 'tr.ban .b-open span{font-size:8px;font-weight:400;color:#B9C7D6;'
			+ 'text-transform:uppercase;letter-spacing:.06em}'
			+ 'tr.ban .b-sub{font-size:8px;color:#C6D3E0;line-height:11px}'
			+ 'tr.ban .b-late{color:#FFC9A8;font-weight:700}'

			+ 'table.op tr.lbl td,table.opv tr.lbl td{background:#E9EDF2;color:#4A5A6C;'
			+ 'font-size:7.5px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;'
			+ 'line-height:10px;border-bottom:1px solid #C9D2DC !important}'

			+ 'table.op tr.dt td,table.opv tr.dt td{border-bottom:1px solid #EDF0F4 !important;'
			+ 'color:#1F2B3E}'
			+ 'table.op tr.dt td.late,table.opv tr.dt td.late{color:#9A2B23;font-weight:700}'
			+ 'table.op tr.dt td.dim,table.opv tr.dt td.dim{color:#7C8B9C}'
			+ 'table.op tr.dt td.strong,table.opv tr.dt td.strong{font-weight:700}'

			+ 'table.op tr.gt td,table.opv tr.gt td{background:#F3F5F8;font-weight:700;'
			+ 'color:#3B4A5C;font-size:8.5px;border-top:1px solid #C9D2DC !important}'

			+ '.op-grand{width:100%;border-collapse:collapse;table-layout:fixed;margin:8px 0 0;'
			+ 'page-break-inside:avoid}'
			+ '.op-grand td{background:#1F2B3E;color:#fff;font-size:10px;font-weight:700;'
			+ 'padding:6px 8px !important;border-top:none !important}'
			+ '.op-grand td.n{text-align:right;font-weight:400}'
			+ '</style>';
	}

	// ---- xls ---------------------------------------------------------------

	/** The vendor summary as a flat sheet — one row per vendor, aging across. */
	private buildVendorXlsHtml(rows: any[]): string {
		const keys = (this.buckets || []).map((b: any) => b.key);
		// Past due spans several buckets, so it earns a column of its own. `not_due`
		// and `no_due_date` do not: they ARE the notdue and nodue buckets by
		// construction, and printing each twice invites someone to add them up.
		const head = ['Vendor', 'Vendor name', 'Terms', 'Documents', 'Oldest due',
			'Document total', 'Allocated', 'Open', 'Past due']
			.concat(keys.map((k: string) => this.bucketLabel(k)));
		const firstNum = 3;
		const num = ' style="text-align:right"';

		let html = '<table border="1" cellspacing="0" cellpadding="4" '
			+ 'style="border-collapse:collapse;font-size:11px">';
		html += '<thead><tr>';
		head.forEach((h, i) => {
			html += '<th style="background-color:#3B4A5C;color:#FFFFFF;text-align:'
				+ (i >= firstNum && i !== 4 ? 'right' : 'left') + '">' + this.esc(h) + '</th>';
		});
		html += '</tr></thead><tbody>';

		for (const r of rows) {
			html += '<tr>'
				+ '<td>' + this.esc(r.supplierno) + '</td>'
				+ '<td>' + this.esc(r.suppname) + '</td>'
				+ '<td>' + this.esc(r.termsname) + '</td>'
				+ '<td' + num + '>' + Number(r.docs) + '</td>'
				+ '<td>' + this.esc(r.oldest_due ? this.shortDate(r.oldest_due) : '') + '</td>'
				+ '<td' + num + '>' + this.money(r.totalamount) + '</td>'
				+ '<td' + num + '>' + this.money(r.allocated) + '</td>'
				+ '<td' + num + '>' + this.money(r.open_balance) + '</td>'
				+ '<td' + num + '>' + this.money(r.past_due) + '</td>'
				+ keys.map((k: string) => '<td' + num + '>'
					+ this.money(Number(r.buckets?.[k]) || 0) + '</td>').join('')
				+ '</tr>';
		}

		const t = this.totalsFromVendors(rows);
		if (t) {
			html += '<tr style="font-weight:bold;background-color:#E2E7EC">'
				+ '<td colspan="3">' + this.esc(this.plural(rows.length, 'vendor')) + '</td>'
				+ '<td' + num + '>' + Number(t.docs) + '</td>'
				+ '<td></td>'
				+ '<td' + num + '>' + this.money(t.totalamount) + '</td>'
				+ '<td' + num + '>' + this.money(t.allocated) + '</td>'
				+ '<td' + num + '>' + this.money(t.open_balance) + '</td>'
				+ '<td' + num + '>' + this.money(t.past_due) + '</td>'
				+ keys.map((k: string) => '<td' + num + '>'
					+ this.money(rows.reduce((a: number, r: any) => a + (Number(r.buckets?.[k]) || 0), 0))
					+ '</td>').join('')
				+ '</tr>';
		}

		return html + '</tbody></table>';
	}

	/**
	 * The document sheet, which wants the opposite of the pdf: one flat, self-
	 * describing row per document, vendor identity repeated onto every line, no
	 * banners and no subtotals inside the data — so it can be sorted, filtered and
	 * pivoted without the reader having to look upward for context.
	 *
	 * Money keeps `$` and thousands separators; apir/print's xls path strips both
	 * before PHPExcel sees the table, so Excel receives numbers it can add up. Which
	 * is also why negatives carry a minus sign rather than accounting parentheses —
	 * "(123.45)" would survive that strip as text.
	 */
	private buildXlsHtml(rows: any[]): string {
		const head = [
			'Vendor', 'Vendor name', 'Terms', 'Type', 'Doc #', 'Reference',
			'Invoice date', 'Due date', 'Days past due', 'Aging', 'Held',
			'Document total', 'Allocated', 'Open',
		];
		const firstNum = 11;
		const num = ' style="text-align:right"';

		let html = '<table border="1" cellspacing="0" cellpadding="4" '
			+ 'style="border-collapse:collapse;font-size:11px">';
		html += '<thead><tr>';
		head.forEach((h, i) => {
			html += '<th style="background-color:#3B4A5C;color:#FFFFFF;text-align:'
				+ (i >= firstNum ? 'right' : 'left') + '">' + this.esc(h) + '</th>';
		});
		html += '</tr></thead><tbody>';

		for (const r of rows) {
			html += '<tr>'
				+ '<td>' + this.esc(r.supplierno) + '</td>'
				+ '<td>' + this.esc(r.suppname) + '</td>'
				+ '<td>' + this.esc(r.termsname) + '</td>'
				+ '<td>' + this.esc(r.typename) + '</td>'
				+ '<td>' + this.esc(r.transno) + '</td>'
				+ '<td>' + this.esc(r.suppreference) + '</td>'
				+ '<td>' + this.esc(this.shortDate(r.trandate)) + '</td>'
				+ '<td>' + this.esc(r.duedate ? this.shortDate(r.duedate) : '') + '</td>'
				// The raw number, not "112 days" — a text column cannot be filtered
				// to "over 60" in Excel, which is the first thing anyone does here.
				+ '<td' + num + '>' + (r.age === null ? '' : r.age) + '</td>'
				+ '<td>' + this.esc(this.bucketLabel(r.bucket)) + '</td>'
				+ '<td>' + (r.hold ? 'Yes' : '') + '</td>'
				+ '<td' + num + '>' + this.money(r.totalamount) + '</td>'
				+ '<td' + num + '>' + this.money(r.allocated) + '</td>'
				+ '<td' + num + '>' + this.money(r.open_balance) + '</td>'
				+ '</tr>';
		}

		const t = this.totalsFromDocs(rows);
		if (t) {
			html += '<tr style="font-weight:bold;background-color:#E2E7EC">'
				+ '<td colspan="11">' + this.esc(this.plural(t.docs, 'document')
					+ ' · ' + this.plural(t.vendors, 'vendor')) + '</td>'
				+ '<td' + num + '>' + this.money(t.totalamount) + '</td>'
				+ '<td' + num + '>' + this.money(t.allocated) + '</td>'
				+ '<td' + num + '>' + this.money(t.open_balance) + '</td>'
				+ '</tr>';
		}

		return html + '</tbody></table>';
	}

	// ---- small helpers -----------------------------------------------------

	private money(v: any): string {
		const n = Number(v) || 0;
		const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		return (n < 0 ? '-$' : '$') + s;
	}

	/** MM/DD/YYYY, matching the grid. The printed report is read by people. */
	private usDate(v: any): string {
		const iso = this.shortDate(v);
		if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) { return iso; }
		return iso.substr(5, 2) + '/' + iso.substr(8, 2) + '/' + iso.substr(0, 4);
	}

	/** "Aug 27, 2026" for the subtitle and scope line — a title is not a data column. */
	private longDate(v: any): string {
		const iso = ('' + (v || '')).substr(0, 10);
		if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) { return iso; }
		const parts = iso.split('-').map((n) => Number(n));
		return new Date(parts[0], parts[1] - 1, parts[2])
			.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
	}

	private shortDate(v: any): string {
		if (!v) { return ''; }
		// Already a calendar date: hand it straight back. new Date('2026-08-18') is
		// parsed as UTC midnight, which prints as the 17th anywhere west of
		// Greenwich — an invoice dated a day early is not a cosmetic problem.
		const iso = ('' + v).substr(0, 10);
		if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) { return iso; }
		const d = new Date(('' + v).replace(' ', 'T'));
		return isNaN(d.getTime()) ? iso : this.isoDate(d);
	}

	private esc(v: any): string {
		return ('' + (v === null || v === undefined ? '' : v))
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	}

	/** Local calendar date. toISOString() would shift Jan 1 back a day west of UTC. */
	private isoDate(d: Date): string {
		const m = ('0' + (d.getMonth() + 1)).slice(-2);
		const day = ('0' + d.getDate()).slice(-2);
		return d.getFullYear() + '-' + m + '-' + day;
	}

	private validDate(v: any): boolean {
		return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
	}

	private oneOf(v: any, allowed: string[], fallback: string): string {
		return allowed.indexOf('' + v) > -1 ? '' + v : fallback;
	}

	private oneOfNum(v: any, allowed: number[], fallback: number): number {
		const n = Number(v);
		return allowed.indexOf(n) > -1 ? n : fallback;
	}

	private csv(v: any): string[] {
		return typeof v === 'string' && v ? v.split(',').filter((s) => !!s) : [];
	}

	/**
	 * Multi-selects travel as comma lists; the server whitelists them into an IN ().
	 * `overrides` is how the export and the per-vendor drill re-ask the same question
	 * with one thing changed, rather than assembling a second, drifting param set.
	 */
	private params(overrides?: any): any {
		const f = this.filterForm.value;
		return {
			from: f.from,
			to: f.to,
			dueby: f.dueby || '',
			doctype: f.doctype,
			supplierno: (f.supplierno || []).join(','),
			bucket: f.bucket || '',
			heldonly: f.heldonly ? 1 : '',
			search: f.search || '',
			limit: f.limit || 500,
			sort: this.sort,
			dir: this.dir,
			vsort: this.vsort,
			vdir: this.vdir,
			...(overrides || {}),
		};
	}
}
